<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { get } from 'svelte/store';
  import { service } from '@frontend/services';
  import { GetCachedToken, me } from '@frontend/user';
  import type { WalletTransactionType } from '@frontend/walletapi';
  import { Transactions, RefreshTransactions } from "@frontend/Transactionlist";
  import { writable } from 'svelte/store';
  import { translate, currentLanguage } from "../Translator";
  import { push } from "svelte-spa-router";

  interface Grupo {
    Id: number;
    Name: string | undefined;
    ParentId: number | undefined;
    Users?: any[];
    level?: number;
  }

  interface Money {
    InCents: number;
  }

  interface User {
    Id: number;
    Username: string;
    FirstName: string;
    LastName: string;
  }

  interface GameServer {
    Id: number;
    Name: string;
  }

  interface PlayersSession {
    Id: number;
    // Agrega más campos si los necesitas
  }

  // Usar la interfaz del API para evitar conflictos de tipos
  import type { TransactionDateResponse } from '@frontend/walletapi';
  import { formatMoney } from './Formats';

  let fechaInicio: string = new Date().toISOString().split('T')[0];
  let fechaFin: string = new Date().toISOString().split('T')[0];
  let dropdownRef: HTMLElement | undefined;
  let menuVisible = false;
  let gruposSeleccionados: Set<number> = new Set();
  let todosSeleccionados = false;
  let grupos: Grupo[] = [];
  let gruposFiltrados: Grupo[] = [];
  let currentGroupId: number | null = null;
  let error: string | null = null;
  let currentPage = 1;
  let pageSize = 20;
  let totalPages = 1;
  let searchFirstName = "";
  let showMoreColumns = false; // Ocultar las columnas adicionales por defecto (mostrar "More")
  let showAllSummaryTypes = false;
  const summaryTypesShort = ['Debit', 'Credit'];
  const summaryTypesFull = ['Debit', 'Credit', 'EndRound', 'Deposit', 'Withdraw', 'CancelBet', 'WithdrawWaiting', 'WithdrawCancel', 'DepositCancel', 'PromoCanceled', 'PromoExpired'];
  let manualSelection = false; // Controlar si el usuario está haciendo selección manual
  let loading = false; // Estado de carga para la búsqueda
  
  // Store para transacciones filtradas por fecha y grupos
  const FilteredTransactions = writable<any[]>([]);

  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      menuVisible = false;
    }
  }

  function toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    menuVisible = !menuVisible;
  }

  // Función para manejar el click en un item
  function handleItemClick(event: MouseEvent): void {
    event.stopPropagation(); // Evitar que el click cierre el dropdown
  }

  function toggleGrupoButton(id: number) {
    if (gruposSeleccionados.has(id)) {
      gruposSeleccionados.delete(id);
    } else {
      gruposSeleccionados.add(id);
    }
    gruposSeleccionados = gruposSeleccionados;
    todosSeleccionados = gruposSeleccionados.size === gruposFiltrados.length;
  }

  function toggleTodosButton() {
    if (todosSeleccionados) {
      gruposSeleccionados.clear();
    } else {
      gruposFiltrados.forEach(grupo => gruposSeleccionados.add(grupo.Id));
    }
    todosSeleccionados = !todosSeleccionados;
    gruposSeleccionados = gruposSeleccionados;
  }

  // Función para manejar la selección de todos los grupos
  function handleSelectAll(event: Event): void {
    event.stopPropagation(); // Evitar que el click se propague
    toggleTodosButton();
  }

  // Función para manejar el cambio de checkbox
  function handleCheckboxChange(event: Event, id: number): void {
    event.stopPropagation(); // Evitar que el click se propague
    toggleGrupoButton(id);
  }

  // Función para buscar transacciones por fecha y grupos seleccionados
  async function searchDateTransactions() {
    try {
      if (!fechaInicio || !fechaFin) {
        error = 'Please select both dates';
        return;
      }

      if (gruposSeleccionados.size === 0) {
        error = 'Please select at least one group';
        return;
      }

      error = null;
      loading = true;

      // Validar que el usuario solo pueda acceder a grupos que tiene permiso para ver
      const meStore = get(me);
      if (!meStore) {
        error = 'User information not available';
        return;
      }

      // Verificar que todos los grupos seleccionados estén en gruposFiltrados (grupos permitidos)
      const gruposPermitidos = new Set(gruposFiltrados.map(g => g.Id));
      const gruposNoPermitidos = Array.from(gruposSeleccionados).filter(id => !gruposPermitidos.has(id));
      
      if (gruposNoPermitidos.length > 0) {
        error = `You don't have permission to access some selected groups: ${gruposNoPermitidos.join(', ')}`;
        return;
      }

      error = null;
      const token = GetCachedToken();
      if (!token) {
        error = 'No token available';
        return;
      }

      if (!meStore?.Id) {
        error = 'User ID not available';
        return;
      }

      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      fin.setHours(23, 59, 59);

      console.log('Buscando transacciones con parámetros:', {
        fechaInicio: inicio.toISOString(),
        fechaFin: fin.toISOString(),
        gruposSeleccionados: Array.from(gruposSeleccionados),
        userId: meStore.Id
      });

      const response = await service.walletapi.searchbydate({
        Token: token,
        Data: {
          Datestart: inicio,
          Dateend: fin,
          GroupIds: Array.from(gruposSeleccionados),
          UserId: meStore.Id,
          Page: 1,
          PageSize: 1000 // Obtener más transacciones para el filtrado
        }
      });

      if (response?.Success && response.Data) {
        console.log('Transacciones obtenidas del API:', response.Data.length);
        console.log('Estructura de la primera transacción del API:', response.Data[0]);
        
        // Log específico para promociones si las hay
        if (response.Data[0]?.PromotionDetails) {
          console.log('Promociones del API (PromotionDetails):', response.Data[0].PromotionDetails.map(p => ({
            amount: p.Amount,
            amountInCents: p.Amount?.InCents,
            amountFormatted: formatMoney(p.Amount)
          })));
        }
        // Actualizar el store de transacciones filtradas con los resultados de la búsqueda
        FilteredTransactions.set(response.Data);
      } else {
        console.log('No se encontraron transacciones o error en la respuesta:', response?.ErrorMessage);
        // Si no hay resultados, limpiar las transacciones filtradas
        FilteredTransactions.set([]);
      }

          } catch (err) {
        console.error('Error buscando transacciones:', err);
        error = 'Error searching transactions';
      } finally {
        loading = false;
      }
  }

  async function cargarGrupos() {
    try {
      const token = GetCachedToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await service.walletapi.groupList({
        Token: token
      });

      if (!response?.Success) {
        throw new Error('Error obtaining groups: ' + (response?.Message || 'Unknown error'));
      }

      if (!response.Data || !Array.isArray(response.Data)) {
        throw new Error('Group data is not in the expected format');
      }

      grupos = response.Data;
      gruposFiltrados = grupos;
      return true;
    } catch (err) {
      console.error('Error cargando grupos:', err);
      error = 'Error loading groups';
      return false;
    }
  }



  // Función para obtener todos los grupos con su estructura jerárquica
  function getAllGroupsWithStructure(grupos: Grupo[]): Grupo[] {
    const result: Grupo[] = [];
    
    function addGroup(grupo: Grupo, level: number = 0) {
      // Crear una nueva copia del grupo con el nivel
      const grupoConNivel = { ...grupo, level };
      result.push(grupoConNivel);
      
      // Buscar hijos donde el ParentId coincida con el Id del grupo actual
      const children = grupos.filter(g => g.ParentId && g.ParentId === grupo.Id);
      // Ordenar los hijos por nombre si es necesario
      children.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
      children.forEach(child => addGroup(child, level + 1));
    }

    // Comenzar con los grupos padre (aquellos sin ParentId)
    const parentGroups = grupos.filter(g => !g.ParentId);
    // Ordenar los grupos padre por nombre si es necesario
    parentGroups.sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
    parentGroups.forEach(grupo => addGroup(grupo));
    
    return result;
  }

  function filtrarGrupos() {
    const meStore = get(me);
    
    // Si es Owner, mostrar todos los grupos sin filtrar
    if (meStore?.Role === "Owner") {
      gruposFiltrados = getAllGroupsWithStructure(grupos);
      return;
    }

    // Si no hay grupo actual o no hay grupos, mostrar todos
    if (!currentGroupId || !grupos.length) {
      gruposFiltrados = grupos;
      return;
    }

    // Encontrar el grupo actual
    const grupoActual = grupos.find(g => g.Id === currentGroupId);
    if (!grupoActual) {
      gruposFiltrados = grupos;
      return;
    }

    // --- Lógica diferenciada para Cashier y Admin ---
    if (meStore?.Role === "Cashier") {
      // Cashier: ve su grupo y su grupo padre
      let gruposDisponibles = [grupoActual];
      if (grupoActual.ParentId) {
        const grupoPadre = grupos.find(g => g.Id === grupoActual.ParentId);
        if (grupoPadre) {
          gruposDisponibles.unshift(grupoPadre);
        }
      }
      gruposFiltrados = gruposDisponibles.map(g => ({ ...g, level: 0 }));
      return;
    }

    // Admin: ve su grupo y todos los subgrupos
    function obtenerHijos(groupId: number): Grupo[] {
      const hijosDirectos = grupos.filter(g => g.ParentId === groupId);
      let todosLosHijos = [...hijosDirectos];
      hijosDirectos.forEach(hijo => {
        todosLosHijos = [...todosLosHijos, ...obtenerHijos(hijo.Id)];
      });
      return todosLosHijos;
    }
    const todosLosGrupos = [
      grupoActual,
      ...obtenerHijos(currentGroupId)
    ];
    gruposFiltrados = todosLosGrupos;
  }


  // Función para establecer las fechas a la última semana
  function setLastWeekDates() {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    
    fechaInicio = lastWeek.toISOString().split('T')[0];
    fechaFin = today.toISOString().split('T')[0];
  }

  // Función para establecer las fechas a las últimas dos semanas
  function setLastTwoWeeksDates() {
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(today.getDate() - 14);
    
    fechaInicio = twoWeeksAgo.toISOString().split('T')[0];
    fechaFin = today.toISOString().split('T')[0];
  }

  // Función para establecer las fechas al último mes
  function setLastMonthDates() {
    const today = new Date();
    const lastMonth = new Date(today);
    lastMonth.setMonth(today.getMonth() - 1);
    
    fechaInicio = lastMonth.toISOString().split('T')[0];
    fechaFin = today.toISOString().split('T')[0];
  }

  // Función para establecer las fechas al día actual
  function setTodayDates() {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    fechaInicio = formattedDate;
    fechaFin = formattedDate;
  }

  // Función para obtener el nombre del grupo con indentación
  function getGroupDisplayName(grupo: Grupo): string {
    const meStore = get(me);
    
    // Si es el grupo actual del usuario
    if (grupo.Id === currentGroupId) {
      return `📁 ${grupo.Name || ''}`;
    }
    
    // Si es Cashier y es el grupo padre
    if (meStore?.Role === "Cashier" && meStore?.GroupId && grupo.Id === grupos.find(g => g.Id === meStore.GroupId)?.ParentId) {
      return `⬆️ ${grupo.Name || ''}`;
    }
    
    // Si es Admin y es un subgrupo
    if (meStore?.Role === "Admin" && grupo.ParentId === currentGroupId) {
      return `└─ ${grupo.Name || ''}`;
    }
    
    // Para otros grupos con nivel
    if (grupo.level && grupo.level > 0) {
      // Para hijos directos (nivel 1), usar una línea
      if (grupo.level === 1) {
        return `└─ ${grupo.Name || ''}`;
      }
      
      // Para niveles más profundos, usar espacios para alinear y una línea
      const spaces = '   '.repeat(grupo.level - 1);
      return `${spaces}└─ ${grupo.Name || ''}`;
    }
    
    // Para grupos raíz sin nivel específico
    return grupo.Name || '';
  }




  // Función auxiliar para calcular el total de promociones
  function calculatePromotionTotal(promotionTransactions: any[], isRealTime: boolean = false): number {
    return promotionTransactions.reduce((sum: number, promo: any) => {
      let value = 0;
      
      // Para PromotionTransactions (WalletTransaction) - usar InitialBalance
      if (promo.InitialBalance?.InCents !== undefined) {
        value = promo.InitialBalance.InCents / 100;
      }
      // Para PromotionDetails (TransactionDateResponse) - usar Amount
      else if (promo.Amount?.InCents !== undefined) {
        value = promo.Amount.InCents / 100;
      }
      // Para valores directos
      else if (promo.Amount !== undefined) {
        value = promo.Amount;
      }
      else if (promo.InitialBalance !== undefined) {
        value = promo.InitialBalance;
      }
      
      // Aplicar corrección de escala solo si es necesario y el valor es muy pequeño
      // Esto evita que se pierdan los decimales en valores normales
      if (value > 0 && value < 1) {
        value = value * 100;
      }
      
      return sum + value;
    }, 0);
  }

  // Función auxiliar para verificar si una transacción tiene promociones
  function hasPromotions(transaction: any): boolean {
    // Verificar si tiene PromotionTransactions (WalletTransaction)
    if (transaction.PromotionTransactions && 
        Array.isArray(transaction.PromotionTransactions) && 
        transaction.PromotionTransactions.length > 0) {
      return true;
    }
    
    // Verificar si tiene PromotionDetails (TransactionDateResponse)
    if (transaction.PromotionDetails && 
        Array.isArray(transaction.PromotionDetails) && 
        transaction.PromotionDetails.length > 0) {
      return true;
    }
    
    return false;
  }

  // Función auxiliar para obtener las promociones de una transacción
  function getPromotions(transaction: any): any[] {
    // Si tiene PromotionTransactions (WalletTransaction)
    if (transaction.PromotionTransactions && 
        Array.isArray(transaction.PromotionTransactions) && 
        transaction.PromotionTransactions.length > 0) {
      return transaction.PromotionTransactions;
    }
    
    // Si tiene PromotionDetails (TransactionDateResponse)
    if (transaction.PromotionDetails && 
        Array.isArray(transaction.PromotionDetails) && 
        transaction.PromotionDetails.length > 0) {
      return transaction.PromotionDetails;
    }
    
    return [];
  }

  // Función auxiliar para obtener el nombre de la promoción
  function getPromotionName(promo: any): string {
    // Para PromotionTransactions (WalletTransaction)
    if (promo.Campaigns?.PromotionName) {
      return promo.Campaigns.PromotionName;
    }
    
    // Para PromotionDetails (TransactionDateResponse)
    if (promo.Name) {
      return promo.Name;
    }
    
    return 'Promoción';
  }

  // Función para verificar si una transacción pertenece a la jerarquía del usuario
  function isTransactionInUserHierarchy(transaction: any): boolean {
    const meStore = get(me);
    
    // Si es Owner, puede ver todas las transacciones
    if (meStore?.Role === "Owner") {
      return true;
    }

    // Si no hay usuario o grupo, no mostrar nada
    if (!meStore?.GroupId) {
      return false;
    }

    // Si la transacción no tiene usuario, no la mostramos
    if (!transaction.User && !transaction.UserLastTransaction) {
      return false;
    }

    const transactionUser = transaction.User || transaction.UserLastTransaction;
    
    // Si el usuario de la transacción no tiene grupo, no lo mostramos
    if (!transactionUser.GroupId) {
      return false;
    }

    const userGroupId = Number(transactionUser.GroupId);
    const myGroupId = Number(meStore.GroupId);

    // Debug logging
    console.log('Verificando transacción:', {
      transactionId: transaction.Id,
      userGroupId,
      myGroupId,
      userRole: meStore.Role,
      transactionUser: transactionUser.FirstName + ' ' + transactionUser.LastName
    });

    // 1. Está en mi grupo
    if (userGroupId === myGroupId) {
      console.log('Transacción en mi grupo:', transaction.Id);
      return true;
    }

    // 2. Está en un grupo hijo directo
    if (transactionUser.Group?.ParentId && Number(transactionUser.Group.ParentId) === myGroupId) {
      console.log('Transacción en grupo hijo directo:', transaction.Id);
      return true;
    }

    // 3. Está en un grupo nieto o más profundo
    let currentGroup = transactionUser.Group;
    while (currentGroup?.ParentId) {
      if (Number(currentGroup.ParentId) === myGroupId) {
        console.log('Transacción en grupo descendiente:', transaction.Id);
        return true;
      }
      // Buscar el siguiente grupo padre
      currentGroup = grupos.find(g => g.Id === currentGroup.ParentId);
    }

    console.log('Transacción NO en mi jerarquía:', transaction.Id);
    return false;
  }

  // Determinar qué transacciones usar: filtradas por API o en tiempo real
  $: transactionsToUse = gruposSeleccionados.size > 0 ? $FilteredTransactions : $Transactions;
  
  // Limpiar transacciones filtradas cuando no hay grupos seleccionados
  $: {
    if (gruposSeleccionados.size === 0 && $FilteredTransactions.length > 0) {
      console.log('Limpiando transacciones filtradas - volviendo a modo tiempo real');
      FilteredTransactions.set([]);
    }
  }
  
  // Filtrar transacciones por fecha, nombre y jerarquía de usuario
  $: todayFilteredTransactions = transactionsToUse.filter(t => {
    const date = new Date(t.TransactionTime).toISOString().split('T')[0];
    // Filtro por fecha
    const inDateRange = date >= fechaInicio && date <= fechaFin;
    // Filtro por nombre (si hay texto en searchFirstName)
    const name = (
      (t.User?.FirstName + ' ' + (t.User?.LastName || '')) ||
      (t.UserLastTransaction?.FirstName + ' ' + (t.UserLastTransaction?.LastName || ''))
    ).toLowerCase();
    const search = searchFirstName.trim().toLowerCase();
    const inName = !search || name.includes(search);
    // Filtro por jerarquía de usuario (solo para transacciones en tiempo real)
    const inUserHierarchy = gruposSeleccionados.size > 0 ? true : isTransactionInUserHierarchy(t);
    
    return inDateRange && inName && inUserHierarchy;
  });

  // Paginación para la lista
  $: paginatedTransactions = todayFilteredTransactions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  $: totalPages = Math.ceil(todayFilteredTransactions.length / pageSize);

  $: {
    if (currentPage > totalPages && totalPages > 0) {
      currentPage = totalPages;
    }
  }

  onMount(async () => {
    try {
      // Verificar autenticación primero
      if (!$me) {
        push("/user/Login");
        return;
      }

      document.addEventListener('click', handleClickOutside);

      // Establecer fechas al día actual
      setTodayDates();

      // Cargar grupos
      const gruposCargados = await cargarGrupos();
      if (!gruposCargados) return;

      // Obtener información del usuario actual
      const meStore = get(me);
      
      if (meStore?.Role === "Owner") {
        // Si es Owner, mostrar todos los grupos
        gruposFiltrados = getAllGroupsWithStructure(grupos);
        // NO seleccionar automáticamente todos los grupos
        todosSeleccionados = false;
        gruposSeleccionados = new Set<number>();
      } else if (meStore?.GroupId) {
        // Si es Admin o Cashier, filtrar grupos según su jerarquía
        currentGroupId = meStore.GroupId;
        filtrarGrupos();
        
        // NO seleccionar automáticamente todos los grupos
        todosSeleccionados = false;
        gruposSeleccionados = new Set<number>();
      }
      
      // Forzar actualización de los sets reactivos
      gruposSeleccionados = gruposSeleccionados;
      
    } catch (err) {
      console.error('Error en onMount:', err);
      error = 'Error initializing page';
    }
  });

  // Suscribirse a cambios en las transacciones para actualización en tiempo real
  $: {
    console.log('Transacciones actualizadas en tiempo real:', $Transactions.length);
    console.log('Transacciones filtradas:', todayFilteredTransactions.length);
    console.log('Usuario actual:', get(me)?.Role, 'Grupo:', get(me)?.GroupId);
    
    // Debug: Mostrar información sobre las transacciones que se están usando
    if (todayFilteredTransactions.length > 0) {
      const firstTransaction = todayFilteredTransactions[0];
              console.log('Primera transacción en la lista:', {
          id: firstTransaction.Id,
          type: firstTransaction.Type,
          hasPromotions: hasPromotions(firstTransaction),
          promotionCount: getPromotions(firstTransaction).length,
          transactionKeys: Object.keys(firstTransaction),
          promotionType: firstTransaction.PromotionTransactions ? 'PromotionTransactions' : 
                        firstTransaction.PromotionDetails ? 'PromotionDetails' : 'None',
          source: gruposSeleccionados.size > 0 ? 'API searchbydate' : 'Real-time Transactions',
          moneyFields: {
            hasMoneyTransaction: !!firstTransaction.MoneyTransaction,
            hasMoneyAmount: !!firstTransaction.MoneyAmount,
            hasMoneyBalance: !!firstTransaction.MoneyBalance,
            moneyAmountValue: firstTransaction.MoneyAmount?.InCents,
            moneyBalanceValue: firstTransaction.MoneyBalance?.InCents
          }
        });
      
      if (hasPromotions(firstTransaction)) {
        const promotions = getPromotions(firstTransaction);
        console.log('Promociones encontradas:', promotions.map(p => ({
          name: getPromotionName(p),
          amount: p.Amount?.InCents,
          balance: p.Balance?.InCents,
          initialBalance: p.InitialBalance?.InCents,
          amountFormatted: formatMoney(p.Amount),
          balanceFormatted: formatMoney(p.Balance),
          initialBalanceFormatted: formatMoney(p.InitialBalance || p.Amount),
          rawAmount: p.Amount,
          rawBalance: p.Balance,
          rawInitialBalance: p.InitialBalance
        })));
      }
    }
  }

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  $: {
    if (currentGroupId) {
      filtrarGrupos();
    }
  }

  // Función para descargar el reporte en Excel
  function downloadExcelReport() {
    if (todayFilteredTransactions.length === 0) {
      alert($translate('No transactions to export'));
      return;
    }

    // Crear el contenido del CSV (Excel puede abrir archivos CSV)
    let csvContent = '';
    
    // Encabezados
    const headers = [
      'ID',
      $translate('Transaction date'),
      $translate('Type'),
      $translate('User'),
      $translate('Amount'),
      $translate('Balance'),
      $translate('Money Amount'),
      $translate('Money Balance'),
      $translate('Promotion Amount'),
      $translate('Promotion Balance'),
      $translate('Promotion Initial Balance')
    ];
    
    // Agregar BOM para Excel
    csvContent += '\ufeff';
    csvContent += headers.join(',') + '\n';
    
    // Datos de las transacciones
    todayFilteredTransactions.forEach(transaction => {
      const row = [
        transaction.Id,
        new Date(transaction.TransactionTime).toLocaleString(),
        transaction.Type,
        transaction.User ? 
          `${transaction.User.FirstName} ${transaction.User.LastName || ''}` :
          transaction.UserLastTransaction ? 
            `${transaction.UserLastTransaction.FirstName} ${transaction.UserLastTransaction.LastName || ''}` : 
            '-',
        formatMoney(transaction.CashableAmount),
        formatMoney(transaction.CashableBalance),
        transaction.MoneyTransaction ? 
          formatMoney(transaction.MoneyTransaction[0]?.Amount) :
          transaction.MoneyAmount ? 
            formatMoney(transaction.MoneyAmount) : 
            '-',
        transaction.MoneyTransaction ? 
          formatMoney(transaction.MoneyTransaction[0]?.Balance) :
          transaction.MoneyBalance ? 
            formatMoney(transaction.MoneyBalance) : 
            '-',
        hasPromotions(transaction) ? 
          getPromotions(transaction).map(p => formatMoney(p.Amount)).join('; ') : 
          $translate('No promotion'),
        hasPromotions(transaction) ? 
          getPromotions(transaction).map(p => formatMoney(p.Balance)).join('; ') : 
          $translate('No promotion'),
        hasPromotions(transaction) ? 
          getPromotions(transaction).map(p => formatMoney(p.InitialBalance || p.Amount)).join('; ') : 
          $translate('No promotion')
      ];
      
      // Escapar comas y comillas en los valores
      const escapedRow = row.map(value => {
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
      
      csvContent += escapedRow.join(',') + '\n';
    });
    
    // Crear el blob y descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    // Generar nombre de archivo con fecha
    const now = new Date();
    const fileName = `transactions_report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}.csv`;
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Función para descargar el resumen en Excel
  function downloadSummaryExcel() {
    if (todayFilteredTransactions.length === 0) {
      alert($translate('No transactions to export'));
      return;
    }

    // Crear el contenido del CSV para el resumen
    let csvContent = '';
    
    // Encabezados del resumen - exactamente como se muestran en la tabla
    const headers = [
      $translate('Type of transaction'),
      $translate('Count'),
      $translate('Amount'),
      $translate('Total')
    ];
    
    // Agregar BOM para Excel
    csvContent += '\ufeff';
    csvContent += headers.join(',') + '\n';
    
    // Datos del resumen
    const summaryTypes = showAllSummaryTypes ? summaryTypesFull : summaryTypesShort;
    
    summaryTypes.forEach(type => {
      const typeTransactions = todayFilteredTransactions.filter(t => t.Type === type);
      const cashableAmount = typeTransactions.reduce((sum, t) => sum + (t.CashableAmount?.InCents || 0), 0);
      const nonCashableAmount = typeTransactions.reduce((sum, t) => sum + (t.NoncashableAmount?.InCents || 0), 0);
      const totalAmount = cashableAmount + nonCashableAmount;
      
      // Escapar valores para evitar problemas con comas en los números
      const row = [
        `"${type}"`,
        typeTransactions.length,
        `"${formatMoney({ InCents: cashableAmount })}"`,
        `"${formatMoney({ InCents: totalAmount })}"`
      ];
      
      csvContent += row.join(',') + '\n';
    });
    
    // Agregar fila de totales - exactamente como se muestra en la tabla
    const totalRow = [
      '"Total"',
      todayFilteredTransactions.length,
      '"-"',
      '"-"'
    ];
    csvContent += totalRow.join(',') + '\n';
    
    // Crear el blob y descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    // Generar nombre de archivo con fecha
    const now = new Date();
    const fileName = `transactions_summary_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}.csv`;
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
</script>

<div class="ui container">
  <!-- Información de permisos del usuario -->
  <div class="ui segment">
    <h3 class="ui header">
      {$translate('Transaction')}
      <div class="sub header">
        <i class="sync icon"></i> {$translate('real time update active')}
      </div>
    </h3>
        
       <!-- <div class="header">Usuario actual: {meStore?.FirstName || ''} {meStore?.LastName || ''}</div>
        <p><strong>Rol:</strong> {getUserRoleDisplayName(meStore?.Role || '')}</p>
         {#if meStore?.GroupId}
          <p><strong>Grupo asignado:</strong> {grupos.find(g => g.Id === meStore.GroupId)?.Name || meStore.GroupId.toString()}</p>
        {/if}
        <p><strong>Descripción de acceso:</strong> {getAccessDescription()}</p>
        <p><strong>Grupos disponibles:</strong> {gruposFiltrados.length}</p>
        <p><strong>Total de transacciones disponibles:</strong> {$Transactions.length}</p>
        <p><strong>Transacciones visibles:</strong> {todayFilteredTransactions.length}</p> -->
        <!-- {#if gruposSeleccionados.size > 0}
          <p><strong>Modo:</strong> Búsqueda por grupos seleccionados ({gruposSeleccionados.size} grupos)</p>
          <p><strong>Fuente de datos:</strong> API de búsqueda por fecha</p>
        {:else}
          <p><strong>Modo:</strong> Actualización en tiempo real</p>
          <p><strong>Fuente de datos:</strong> Transacciones en tiempo real</p>
        {/if} -->
    
      
      <!-- Lista de grupos disponibles -->

  </div> 

  <div class="ui segment">
    <div class="ui form">
      <div class="fields">
        <div class="field">
          <label>{$translate('Start Date')}</label>
          <div class="ui input">
            <input type="date" bind:value={fechaInicio}>
          </div>
        </div>
        <div class="field">
          <label>{$translate('End Date')}</label>
          <div class="ui input">
            <input type="date" bind:value={fechaFin}>
          </div>
        </div>
        <div class="field">
          <label>{$translate('Groups')}</label>
          <div 
            class="ui fluid multiple search selection dropdown" 
            style="width: 100%; margin-bottom: 1em; position: relative;"
            bind:this={dropdownRef}
            on:click={toggleMenu}
          >
            <input type="hidden" name="groups">
            <i class="dropdown icon"></i>
            <div class="default text" style="font-size: 0.9em;">Multiple groups ({gruposSeleccionados.size})</div>
            <div class="menu" class:visible={menuVisible}>
              <div class="item" on:click={handleSelectAll}>
                <div class="ui checkbox">
                  <input 
                    type="checkbox" 
                    checked={todosSeleccionados}
                  >
                </div>
                <span style="font-size: 0.9em;">{$translate('Select All')}</span>
              </div>
              {#each gruposFiltrados as grupo}
                <div 
                  class="item"
                  class:selected={gruposSeleccionados.has(grupo.Id)}
                  style="display: flex; align-items: center; padding: 0.4em 0.8em;"
                  on:click={handleItemClick}
                >
                  <div class="ui checkbox">
                    <input 
                      type="checkbox" 
                      checked={gruposSeleccionados.has(grupo.Id)}
                      on:change={(e) => handleCheckboxChange(e, grupo.Id)}
                    >
                  </div>
                  <span style="flex-grow: 1; white-space: pre; font-size: 0.9em;">
                    {getGroupDisplayName(grupo)}
                    {#if grupo.Id === currentGroupId}
                      <div class="ui mini label" style="background-color: #21ba45; color: white; margin-left: 0.5em;">
                        {$translate('Current')}
                      </div>
                    {/if}
                  </span>
                </div>
              {/each}
            </div>
          </div>
        </div>
        <div class="field search-button-field">
          <label>&nbsp;</label>
          <button 
            class="ui primary button large fluid"
            on:click={searchDateTransactions}
            disabled={loading || !fechaInicio || !fechaFin || gruposSeleccionados.size === 0}
          >
            {#if loading}
              <i class="spinner loading icon"></i> {$translate('Loading')}...
            {:else}
              <i class="search icon"></i> {$translate('Search')}
            {/if}
          </button>
        </div>
      </div>
      <div class="field">
        <button class="ui compact button" on:click={setLastWeekDates}>{$translate('1 Week')}</button>
        <button class="ui compact button" on:click={setLastTwoWeeksDates}>{$translate('2 Weeks')}</button>
        <button class="ui compact button" on:click={setLastMonthDates}>{$translate('1 Month')}</button>
      </div>
      <div class="field">
        <label> {$translate('Search by name')}</label>
        <div class="ui action input" style="max-width: 300px;">
          <input 
            type="text" 
            bind:value={searchFirstName} 
            placeholder="Nombre de jugador..."
            autocomplete="off"
          >
          <button class="ui icon button" on:click={() => searchFirstName = ""}>
            <i class="times icon"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="ui segment">
    <h3 class="ui header">{$translate('Summary by Type of Transaction')}</h3>
    <div class="ui right floated">
      <button class="ui compact button" on:click={() => showAllSummaryTypes = !showAllSummaryTypes}>
        {showAllSummaryTypes ? $translate('Less') : $translate('More')}
      </button>
      <button class="ui compact green button" on:click={downloadSummaryExcel} disabled={todayFilteredTransactions.length === 0}>
        <i class="download icon"></i> {$translate('Download Summary')}
      </button>
    </div>
    {#if todayFilteredTransactions.length > 0}
      <table class="ui celled table summary-table">
        <thead>
          <tr>
            <th>{$translate('Type of transaction')}</th>
            <th>{$translate('Count')}</th>
            <th>{$translate('Amount')}</th>
            <th>{ $translate('Total')}</th>
          </tr>
        </thead>
        <tbody>
          {#each (showAllSummaryTypes ? summaryTypesFull : summaryTypesShort) as type}
            {@const typeTransactions = todayFilteredTransactions.filter(t => t.Type === type)}
            {@const cashableAmount = typeTransactions.reduce((sum, t) => sum + (t.CashableAmount?.InCents || 0), 0)}
            {@const nonCashableAmount = typeTransactions.reduce((sum, t) => sum + (t.NoncashableAmount?.InCents || 0), 0)}
            {@const totalAmount = cashableAmount + nonCashableAmount}
            <tr>
              <td data-label="{$translate('Type of transaction')}">{type}</td>
              <td data-label="{$translate('Count')}">{typeTransactions.length}</td>
              <td data-label="{$translate('Amount')}" class:negative={cashableAmount < 0} class:positive={cashableAmount > 0}>
                {formatMoney({ InCents: cashableAmount })}
              </td>
              <td data-label="{$translate('Total')}" class:negative={totalAmount < 0} class:positive={totalAmount > 0}>
                {formatMoney({ InCents: totalAmount })}
              </td>
            </tr>
          {/each}
          <tr class="total-row">
            <td data-label="Total"><strong>Total</strong></td>
            <td data-label="{$translate('Count')}"><strong>{todayFilteredTransactions.length}</strong></td>
            <td data-label="{$translate('Amount')}">-
              <!-- <strong>
                {formatMoney({
                  InCents: todayFilteredTransactions.reduce((sum, t) => sum + (t.CashableAmount?.InCents || 0), 0)
                })}
              </strong> -->
            </td>
            <td data-label="{$translate('Total')}">-
              <!-- <strong>
                {formatMoney({
                  InCents: todayFilteredTransactions.reduce((sum, t) => 
                    sum + (t.CashableAmount?.InCents || 0) + (t.NoncashableAmount?.InCents || 0), 0)
                })}
              </strong> -->
            </td>
          </tr>
        </tbody>
      </table>
    {:else}
      <div class="ui message info">
        {$translate('No transactions were found for the selected dates.')}
      </div>
    {/if}
  </div>

  <div class="ui segment">
    <h3 class="ui header">{$translate('Transaction List')}</h3>
    {#if todayFilteredTransactions.length > 0}
      <div class="ui right floated">
        <button class="ui compact button" on:click={() => showMoreColumns = !showMoreColumns}>
          {showMoreColumns ? $translate('Less') : $translate('More')}
        </button>
        <button class="ui compact blue button" on:click={downloadExcelReport} disabled={todayFilteredTransactions.length === 0}>
          <i class="download icon"></i> {$translate('Download Report')}
        </button>
      </div>
      <table class="ui celled table responsive">
        <thead>
          <tr>
            <th>#</th>
            <th>{$translate('Transaction date')}</th>
            <th>{$translate('Type')}</th>
            <th>{$translate('User')}</th>
            <th>{$translate('Amount')}</th>
            <th>{$translate('Balance')}</th>
            {#if showMoreColumns}
              <th>{$translate('Money Amount')}</th>
              <th>{$translate('Money Balance')}</th>
              <th>{$translate('Promotion')}</th>
            {/if}
          </tr>
        </thead>
        <tbody>
          {#each paginatedTransactions as transaction}
            {@html (() => { console.log('Transaction AZUCEL:', transaction); return ''; })()}
            <tr>
              <td data-label="ID">{transaction.Id}</td>
              <td data-label="{$translate('Transaction date')}">{new Date(transaction.TransactionTime).toLocaleString()}</td>
              <td data-label="{$translate('Type')}">{transaction.Type}</td>
              <td data-label="{$translate('User')}">
                {#if transaction.User}
                  {transaction.User.FirstName} {transaction.User.LastName || ''}
                {:else if transaction.UserLastTransaction}
                  {transaction.UserLastTransaction.FirstName} {transaction.UserLastTransaction.LastName || ''}
                {:else}
                  -
                {/if}
              </td>
              <td data-label="{$translate('Amount')}" class:negative={(transaction.CashableAmount?.InCents || 0) < 0} class:positive={(transaction.CashableAmount?.InCents || 0) > 0}>
                {formatMoney(transaction.CashableAmount)}
              </td>
              <td data-label="{$translate('Balance')}"> {formatMoney(transaction.CashableBalance)} </td>
              {#if showMoreColumns}
              <td data-label="{$translate('Money Amount')}"> 
                {#if transaction.MoneyTransaction}
                  {formatMoney(transaction.MoneyTransaction?.[0]?.Amount)}
                {:else if transaction.MoneyAmount}
                  {formatMoney(transaction.MoneyAmount)}
                {:else}
                  -
                {/if}
              </td>
              <td data-label="{$translate('Money Balance')}"> 
                {#if transaction.MoneyTransaction}
                  {formatMoney(transaction.MoneyTransaction?.[0]?.Balance)}
                {:else if transaction.MoneyBalance}
                  {formatMoney(transaction.MoneyBalance)}
                {:else}
                  -
                {/if}
              </td>
              <td data-label="{$translate('Promotion')}">
                {#if hasPromotions(transaction)}
                  <table>
                    <thead>
                      <tr>
                        <!-- <th>Promoción</th> -->
                        <th>Monto</th>
                        <th>Balance</th>
                        <th>Inicial</th>
                      </tr>
                    </thead>
                    <tbody>
                      {#each getPromotions(transaction) as promo}
                        <tr>
                          <!-- <td>{getPromotionName(promo)}</td> -->
                          <td>{formatMoney(promo.Amount)}</td>
                          <td>{formatMoney(promo.Balance)}</td>
                          <td>{formatMoney(promo.InitialBalance)}</td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                {:else}
                  {$translate('No promotion')}
                {/if}
              </td>
            
              {/if}
            </tr>
          {/each}
        </tbody>
        <tfoot>
          <tr>
            <th colspan={showMoreColumns ? 9 : 6}>
              <div class="ui right floated pagination menu">
                <a 
                  class="icon item" 
                  class:disabled={currentPage === 1}
                  on:click={() => currentPage = Math.max(1, currentPage - 1)}
                >
                  <i class="left chevron icon"></i>
                  {$translate('Previous')}
                </a>
                <div class="item">
                  {$translate('Page')} {currentPage} of {totalPages}
                </div>
                <a 
                  class="icon item" 
                  class:disabled={currentPage === totalPages}
                  on:click={() => currentPage = Math.min(totalPages, currentPage + 1)}
                >
                  {$translate('Next')}
                  <i class="right chevron icon"></i>
                </a>
              </div>
              <div class="ui left floated">
                <!-- Total: {todayFilteredTransactions.length} Transactions -->
                {$translate('Total')}-{todayFilteredTransactions.length}-{$translate('Transactions')}
              </div>
            </th>
          </tr>
        </tfoot>
      </table>
    {:else}
      <div class="ui message info">
        {$translate('No transactions were found for the selected dates.')}
      </div>
    {/if}
  </div>
</div>

<style>
  /* Ensure form container handles dropdown overflow properly */
  .ui.form {
    position: relative;
    z-index: 1;
    overflow: visible;
  }

  /* Ensure main content can scroll */
  :global(.article) {
    overflow-y: auto !important;
    overflow-x: auto !important;
  }

  /* Ensure page content is scrollable */
  .ui.container {
    min-height: auto;
    overflow: visible;
  }

  .ui.container {
    padding: 1rem;
    width: 100%;
    max-width: none;
    overflow: visible;
  }

  .ui.segment {
    background: #fff;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: .28571429rem;
    box-shadow: 0 1px 2px 0 rgba(34,36,38,.15);
    margin: 1rem 0;
    padding: 1em;
  }

  .fields {
    display: flex;
    gap: 1em;
  }

  .field {
    flex: 1;
  }

  .field label {
    display: block;
    color: rgba(0,0,0,.87);
    font-size: .92857143em;
    font-weight: 700;
    margin: 0 0 .28571429rem;
  }

  .ui.input {
    width: 100%;
  }

  .ui.input input {
    width: 100%;
    padding: .67857143em 1em;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: .28571429rem;
    font-size: 1em;
    color: rgba(0,0,0,.87);
    background: #fff;
    line-height: 1.21428571em;
  }

  .negative {
    color: #db2828;
  }
  .positive {
    color: #21ba45;
  }
  table {
    font-size: 0.9em;
  }
  td {
    white-space: nowrap;
  }

  /* Center align all table content */
  .ui.celled.table th,
  .ui.celled.table td {
    text-align: center !important;
  }

  /* Keep the first column (Type of transaction) left-aligned for better readability */
  .ui.celled.table th:first-child,
  .ui.celled.table td:first-child {
    text-align: left !important;
  }

  /* Ensure summary table columns are properly centered */
  .summary-table th,
  .summary-table td {
    text-align: center !important;
  }

  .summary-table th:first-child,
  .summary-table td:first-child {
    text-align: left !important;
  }

  /* Center all content in the transaction list table */
  .ui.table.responsive th,
  .ui.table.responsive td {
    text-align: center !important;
  }

  .ui.pagination.menu {
    margin: 0;
    box-shadow: none;
  }

  .ui.pagination.menu .item {
    min-width: 2.5em;
    text-align: center;
    padding: 0.5em 1em;
  }

  .ui.pagination.menu .item:hover {
    background-color: rgba(0,0,0,.05);
  }

  .ui.pagination.menu .item.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Asegura que las subtablas tengan el mismo tamaño de letra que la tabla principal */
  table table {
    font-size: inherit;
  }

  /* Estilos para el dropdown de grupos */
  .ui.dropdown {
    min-height: 2.2em !important;
    position: relative;
    padding: 0.4em 0.8em;
    cursor: pointer;
    z-index: 10000 !important;
    /* Ensure dropdown container doesn't overflow */
    overflow: visible;
    isolation: isolate;
  }

  .ui.dropdown .menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 0.28571429rem;
    box-shadow: 0 2px 4px 0 rgba(34,36,38,.15);
    z-index: 10001 !important;
    max-height: 250px;
    overflow-y: auto;
    margin-top: 0.3em !important;
    width: 100%;
    /* Ensure dropdown stays within its container */
    position: relative;
    z-index: 10001 !important;
  }

  .ui.dropdown .menu > .item {
    position: relative;
    z-index: 10002 !important;
    background: white;
    padding: 0.4em 0.8em !important;
    line-height: 1.2;
  }

  .ui.dropdown .menu > .item:hover {
    background: rgba(0,0,0,.05);
  }

  .ui.dropdown .menu > .item.selected {
    background: rgba(0,0,0,.03) !important;
  }

  .ui.dropdown .text {
    margin-right: 2em;
    line-height: 1.2;
    display: flex !important;
    align-items: center !important;
  }

  .ui.checkbox {
    margin-right: 8px !important;
    min-width: 14px !important;
  }

  .ui.checkbox input[type="checkbox"] {
    width: 14px !important;
    height: 14px !important;
  }

  .ui.dropdown .menu > .item span {
    font-family: monospace;
    padding: 0.2em 0;
  }

  .ui.dropdown .menu.visible {
    display: block;
  }

  /* Ajustar el tamaño del contenedor del dropdown */
  .field .ui.dropdown {
    min-width: 200px;
    max-width: 250px;
    /* Ensure dropdown stays within its field container */
    position: relative;
    z-index: 10000;
    overflow: visible;
  }

  /* Ensure dropdown field container doesn't overflow */
  .field {
    position: relative;
    z-index: 1;
    overflow: visible;
  }

  .ui.table {
    position: relative;
    z-index: 1;
  }

  .ui.checkbox {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    margin: 0 !important;
    margin-right: 10px !important;
    min-width: 16px !important;
  }

  .ui.checkbox input[type="checkbox"] {
    position: relative !important;
    margin: 0 !important;
    width: 16px !important;
    height: 16px !important;
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
    z-index: 1 !important;
    -webkit-appearance: checkbox !important;
    appearance: checkbox !important;
    cursor: pointer !important;
  }

  /* Estilos de los items del menú */
  .ui.dropdown .menu > .item {
    padding: 0.5em 1em !important;
    display: flex !important;
    align-items: center !important;
    cursor: pointer;
  }

  .ui.dropdown .menu > .item:hover {
    background: rgba(0,0,0,.05);
  }

  .ui.dropdown .menu > .item.selected {
    background: rgba(0,0,0,.03) !important;
  }

  /* Etiquetas */
  .ui.mini.label {
    margin-left: 0.5em;
    padding: 0.2em 0.5em;
    font-size: 0.7em;
    vertical-align: middle;
    display: inline-block;
    background: #e8e8e8;
    border-radius: 0.28571429rem;
  }

  /* Estilos para el mensaje de información */
  .ui.info.message {
    background-color: #f8ffff;
    color: #276f86;
    border: 1px solid #a9d5de;
  }



  /* Estilos para las columnas adicionales */

  /* Estilos para las sub-tablas de promociones */
  .ui.table table {
    width: 100%;
    border-collapse: collapse;
    margin: 0;
  }

  .ui.table table th,
  .ui.table table td {
    padding: 0.3em 0.5em;
    font-size: 0.85em;
    border: 1px solid rgba(34,36,38,.1);
  }

  .ui.table table th {
    background-color: rgba(0,0,0,.03);
    font-weight: 600;
  }

  /* Responsive styles */
  @media (max-width: 768px) {
    .ui.container {
      padding: 0.5rem !important;
    }

    .ui.segment {
      padding: 1rem !important;
      margin-bottom: 1rem !important;
    }

    /* Form fields responsive */
    .ui.form .fields {
      flex-direction: column !important;
    }

    .ui.form .field {
      margin-bottom: 1rem !important;
    }

    .ui.form .field label {
      font-size: 0.9rem !important;
      margin-bottom: 0.5rem !important;
    }

    .ui.input {
      width: 100% !important;
    }

    .ui.input input {
      font-size: 0.9rem !important;
      padding: 0.5rem !important;
    }

    /* Date inputs specific styles for mobile */
    .ui.input input[type="date"] {
      font-size: 0.85rem !important;
      padding: 0.4rem !important;
      min-height: 2.5rem !important;
    }

    /* Improve date input visibility */
    .ui.input input[type="date"]::-webkit-calendar-picker-indicator {
      background-color: transparent !important;
      cursor: pointer !important;
      padding: 0.2rem !important;
    }

    .ui.input input[type="date"]::-webkit-datetime-edit {
      padding: 0 !important;
    }

    .ui.input input[type="date"]::-webkit-datetime-edit-fields-wrapper {
      padding: 0 !important;
    }

    .ui.input input[type="date"]::-webkit-datetime-edit-text {
      padding: 0 0.1rem !important;
    }

    .ui.input input[type="date"]::-webkit-datetime-edit-month-field,
    .ui.input input[type="date"]::-webkit-datetime-edit-day-field,
    .ui.input input[type="date"]::-webkit-datetime-edit-year-field {
      padding: 0 0.1rem !important;
    }

    /* Dropdown responsive */
    .ui.dropdown {
      width: 100% !important;
      min-width: auto !important;
      max-width: none !important;
      z-index: 10000 !important;
    }

    .ui.dropdown .menu {
      max-height: 200px !important;
      overflow-y: auto !important;
      z-index: 10001 !important;
    }

    /* Buttons responsive */
    .ui.button {
      width: 100% !important;
      margin-bottom: 0.5rem !important;
      padding: 0.75rem !important;
      font-size: 1rem !important;
    }

    .ui.compact.button {
      width: auto !important;
      margin-right: 0.5rem !important;
      margin-bottom: 0.5rem !important;
    }

    /* Table responsive */
    .ui.table {
      font-size: 0.8rem !important;
    }

    .ui.table th,
    .ui.table td {
      padding: 0.5rem 0.25rem !important;
    }

    /* Responsive table styles */
    .ui.table.responsive {
      display: block !important;
      width: 100% !important;
      overflow-x: auto !important;
      white-space: nowrap !important;
    }

    .ui.table.responsive thead,
    .ui.table.responsive tbody,
    .ui.table.responsive tr,
    .ui.table.responsive th,
    .ui.table.responsive td {
      display: block !important;
    }

    .ui.table.responsive thead tr {
      position: absolute !important;
      top: -9999px !important;
      left: -9999px !important;
    }

    .ui.table.responsive tr {
      border: 1px solid #ccc !important;
      margin-bottom: 0.5rem !important;
      padding: 0.5rem !important;
      background: #f9f9f9 !important;
    }

    .ui.table.responsive td {
      border: none !important;
      position: relative !important;
      padding-left: 50% !important;
      text-align: left !important;
      white-space: normal !important;
    }

    .ui.table.responsive td:before {
      content: attr(data-label) ": " !important;
      position: absolute !important;
      left: 0.5rem !important;
      width: 45% !important;
      padding-right: 0.5rem !important;
      white-space: nowrap !important;
      font-weight: bold !important;
    }

    /* Hide less important columns on mobile */
    .ui.table th:nth-child(4),
    .ui.table td:nth-child(4),
    .ui.table th:nth-child(6),
    .ui.table td:nth-child(6) {
      display: none !important;
    }

    /* Summary table responsive */
    .ui.table.summary-table th,
    .ui.table.summary-table td {
      padding: 0.5rem 0.25rem !important;
      font-size: 0.8rem !important;
    }

    /* Header responsive */
    .ui.header {
      font-size: 1.5rem !important;
    }

    .ui.header .sub.header {
      font-size: 0.9rem !important;
    }

    /* Search input responsive */
    .ui.action.input {
      max-width: none !important;
      width: 100% !important;
    }

    .ui.action.input input {
      width: calc(100% - 50px) !important;
    }

    .ui.action.input .ui.button {
      width: 50px !important;
      margin: 0 !important;
    }
  }

  /* Tablet styles */
  @media (min-width: 769px) and (max-width: 1024px) {
    .ui.container {
      padding: 1rem !important;
    }

    .ui.form .fields {
      flex-wrap: wrap !important;
    }

    .ui.form .field {
      flex: 1 1 45% !important;
      margin-bottom: 1rem !important;
    }

    .ui.table {
      font-size: 0.9rem !important;
    }

    .ui.table th,
    .ui.table td {
      padding: 0.6rem 0.4rem !important;
    }

    /* Dropdown z-index for tablet */
    .ui.dropdown {
      z-index: 10000 !important;
    }

    .ui.dropdown .menu {
      z-index: 10001 !important;
    }

    /* Date inputs for tablet */
    .ui.input input[type="date"] {
      font-size: 0.9rem !important;
      padding: 0.5rem !important;
      min-height: 2.8rem !important;
    }
  }
</style>